import { Box, Button, Flex, Stack, Tooltip, useToast } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { Workshop } from 'api/types';
import { AxiosError } from 'axios';
import Link from 'components/Link';
import useCreateWorkshopVerificationMutation from 'hooks/mutations/useCreateWorkshopVerificationMutation';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { IdentificationResultIcon } from '../../components/Icons';
import AlertModal from '../../components/modals/AlertModal';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/Table/DataTable';
import { dateTimeTransformer, dateTimezoneTransformer } from '../../helpers/dateTransformer';
import { defaultToastOptions } from '../../helpers/manageErrorResponse';
import useDeleteWorkshopVerificationRequestMutation from '../../hooks/mutations/useDeleteWorkshopVerificationRequestMutation';
import { useWorkshopVerificationRequestsQuery } from '../../hooks/useWorkshopVerificationRequestsQuery';
import { ContentCard } from '../../layout/ContentCard';

const WorkshopVerificationOverviewCard = (props: { isLoading: boolean; workshopVerificationCodes: object; workshop: Workshop }) => {
  const { workshopVerificationCodes, isLoading, workshop } = props;
  const workshopId = workshop?.id;

  const initialFilters = {
    itemsPerPage: 10,
    page: 1,
    currentPage: 1,
    customer_id: workshopId,
  };

  const [page, setPage] = useState(initialFilters.page);
  const [itemsPerPage, setItemsPerPage] = useState(initialFilters.itemsPerPage);

  const workshopVerificationRequestsQuery = useWorkshopVerificationRequestsQuery({ page, itemsPerPage, customer_id: workshopId });

  const verifications = workshopVerificationRequestsQuery?.data?.data || [];
  const currentPage = workshopVerificationRequestsQuery?.data?.meta?.current_page;
  const totalPages = workshopVerificationRequestsQuery.data?.meta?.last_page;

  useEffect(() => {
    setPage(1);
  }, [itemsPerPage]);

  const { t } = useTranslation();

  const deleteWorkshopVerificationRequestMutation = useDeleteWorkshopVerificationRequestMutation(['workshop', workshop?.id]);
  const toast = useToast(defaultToastOptions);
  const createWorkshopVerificationMutation = useCreateWorkshopVerificationMutation({
    workshop_id: workshopId,
  });
  const enabledBttn =
    // @ts-ignore
    (workshop?.verifications?.length === 0 ||
      // @ts-ignore
      (workshop?.verifications?.length > 0 && workshop?.verifications[0]?.status === 700) ||
      // @ts-ignore
      (workshop?.verifications?.length > 0 && workshop?.verifications[0]?.status === 900)) &&
    workshop?.status !== 600 &&
    // don’t allow new verifications if a DUR is open
    !(workshop?.update_requests[0]?.status === 200);

  const columns = useMemo(
    () => [
      {
        Header: 'ID',
        id: 'button',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link to={`/app/op/workshop-verification-request/${row.original.id}`} data-test-id={'edit-' + row.index}>
              {row.original.user_readable_id}
            </Link>
          );
        },
      },
      {
        Header: t('forms:status.label'),
        accessor: 'status',
        shouldFilter: true,
        // @ts-ignore
        Cell: ({ value }) => (
          <StatusTag
            resourceKey={'workshop_verification_request'}
            // @ts-ignore
            statusCodes={workshopVerificationCodes}
            currentStatus={value}
          />
        ),
      },
      {
        Header: t('forms:assignee.label'),
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const assignee = row?.original?.process_by?.name;
          return <Box> {assignee ?? '-'}</Box>;
        },
      },
      {
        Header: t('forms:created_at.label'),
        accessor: 'created_at',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Tooltip label={dateTimeTransformer(row?.original?.created_at) ?? '-'}>
              <Box> {dateTimezoneTransformer(row?.original?.created_at, 'Europe/Vienna', false) ?? '-'}</Box>
            </Tooltip>
          );
        },
      },
      {
        Header: t('forms:completed_at.label'),
        accessor: 'completed_at',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Tooltip label={dateTimeTransformer(row?.original?.completed_at) ?? '-'}>
              <Box> {dateTimezoneTransformer(row?.original?.completed_at, 'Europe/Vienna', false) ?? '-'}</Box>
            </Tooltip>
          );
        },
      },
      {
        Header: t('workshop_verification_request:associated_dur'),
        accessor: 'workshop_update_request_id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const workshopUpdateRequestId = row?.original?.data_update_request_id;

          return (
            <>
              {workshopUpdateRequestId ? (
                <Link to={`/app/op/workshop-update-request/${workshopUpdateRequestId}`} data-test-id={'edit-' + row.index}>
                  {t('workshop_verification_request:associated_dur')}
                </Link>
              ) : (
                '_'
              )}
            </>
          );
        },
      },
      {
        Header: t('workshop_verification_request:requested_by.label'),
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const submittedByCustomer = row?.original?.submitted_by_customer;
          return (
            <Box>
              {submittedByCustomer ? t('workshop_verification_request:requested_by.customer') : t('workshop_verification_request:requested_by.ops')}
            </Box>
          );
        },
      },
      {
        id: 'delete',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          if (!row.original.workshop_user_id) {
            return (
              <Flex alignContent={'flex-end'}>
                {workshop.status !== 600 && (
                  <Button
                    ml={'auto'}
                    onClick={() => {
                      NiceModal.show(AlertModal as any, {
                        onSubmit: () =>
                          deleteWorkshopVerificationRequestMutation.mutate(row.original.id, {
                            onSuccess: () => {
                              window.location.reload();
                            },
                            onError: (error: AxiosError<{ message: string }>) => {
                              toast({
                                title: error.response?.data?.message ?? t('errors:general'),
                                status: 'error',
                              });
                            },
                          }),
                        children: t('workshop_verification_request:modals.delete.description'),
                        content: {
                          header: t('workshop_verification_request:modals.delete.header'),
                          footer: {
                            buttons: {
                              cancelCaption: t('common:cancel'),
                              actionCaption: t('common:delete'),
                            },
                          },
                        },
                      });
                    }}
                  >
                    {t('common:delete')}
                  </Button>
                )}
              </Flex>
            );
          }
        },
      },
    ],
    [deleteWorkshopVerificationRequestMutation, t, toast, workshop.status, workshopVerificationCodes],
  );

  return (
    <ContentCard
      isLoading={workshopVerificationRequestsQuery.isLoading || isLoading}
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('workshop_verification_request:header')}
      data-test-id="verifications-overview-card"
    >
      <Flex justifyContent={'flex-end'}>
        <Button
          disabled={!enabledBttn}
          mr={3}
          variant="primary"
          as={enabledBttn ? RouterLink : undefined}
          to={`/app/op/workshops/${workshop?.id}/workshop-verification-request/create`}
        >
          {t('workshop_verification_request:verify_manually')}
        </Button>
        <Button
          disabled={!enabledBttn}
          variant="primary"
          onClick={() => {
            NiceModal.show(AlertModal as any, {
              onSubmit: () => {
                createWorkshopVerificationMutation.mutate(undefined, {
                  onSuccess: () => {
                    window.location.reload();
                  },
                });
              },

              children: <Trans i18nKey={`workshop_verification_request:re_verification_warning`} />,
              content: {
                header: t('workshop_verification_request:re_verification_header'),
                footer: {
                  buttons: {
                    cancelCaption: t('workshop_verification_request:abort'),
                    actionCaption: t('workshop_verification_request:re_verification'),
                  },
                },
              },
            });
          }}
        >
          {t('workshop_verification_request:request_verification')}
        </Button>
      </Flex>

      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w="full">
          <DataTable
            data-test-id="verifications-table"
            // @ts-ignore
            hasSearchInput={false}
            data={verifications}
            resetFilter={() => {
              setPage(1);
              setItemsPerPage(initialFilters?.itemsPerPage);
            }}
            // @ts-ignore
            hasFilters={false}
            columns={columns}
            loading={isLoading}
            currentPage={currentPage ? currentPage - 1 : 1}
            perPage={itemsPerPage}
            setPage={setPage}
            setPerPage={setItemsPerPage}
            total={totalPages}
            // @ts-ignore
            isMoreThenOnePage={true}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default WorkshopVerificationOverviewCard;
